import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider, useTheme } from './contexts/ThemeContext';
import './App.css';
import { Toaster } from './components/ui/sonner';

// Lazy load pages
const AuthPage = lazy(() => import('./pages/AuthPage'));
const ForgotPasswordPage = lazy(() => import('./pages/ForgotPasswordPage'));
const LegalPage = lazy(() => import('./pages/LegalPage'));
const UserDashboard = lazy(() => import('./pages/user/Dashboard'));
const BuyPage = lazy(() => import('./pages/user/BuyPage'));
const StakingPage = lazy(() => import('./pages/user/StakingPage'));
const LeaderboardPage = lazy(() => import('./pages/user/LeaderboardPage'));
const ReferralsPage = lazy(() => import('./pages/user/ReferralsPage'));
const WithdrawalsPage = lazy(() => import('./pages/user/WithdrawalsPage'));
const ProfilePage = lazy(() => import('./pages/user/ProfilePage'));
const AdminDashboard = lazy(() => import('./pages/admin/AdminDashboard'));
const AdminUsers = lazy(() => import('./pages/admin/Users'));
const AdminPlans = lazy(() => import('./pages/admin/Plans'));
const AdminWithdrawals = lazy(() => import('./pages/admin/Withdrawals'));
const AdminReferrals = lazy(() => import('./pages/admin/Referrals'));
const AdminAuditLogs = lazy(() => import('./pages/admin/AuditLogs'));

const LoadingSpinner = () => (
  <div className="flex items-center justify-center min-h-screen bg-background">
    <div className="spinner"></div>
  </div>
);

const ProtectedRoute = ({ children, adminOnly = false }) => {
  const { user, loading } = useAuth();

  if (loading) return <LoadingSpinner />;
  if (!user) return <Navigate to="/auth" replace />;
  if (adminOnly && user.role !== 'admin') return <Navigate to="/dashboard" replace />;

  return children;
};

const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) return <LoadingSpinner />;
  if (user) {
    return <Navigate to={user.role === 'admin' ? '/admin' : '/dashboard'} replace />;
  }

  return children;
};

// Root entry – no landing page. Route straight into the app.
const RootRedirect = () => {
  const { user, loading } = useAuth();
  if (loading) return <LoadingSpinner />;
  if (!user) return <Navigate to="/auth" replace />;
  return <Navigate to={user.role === 'admin' ? '/admin' : '/dashboard'} replace />;
};

function AppRoutes() {
  return (
    <Suspense fallback={<LoadingSpinner />}>
      <Routes>
        <Route path="/" element={<RootRedirect />} />

        <Route path="/auth" element={
          <PublicRoute>
            <AuthPage />
          </PublicRoute>
        } />

        <Route path="/forgot-password" element={<ForgotPasswordPage />} />

        <Route path="/terms" element={<LegalPage type="terms" />} />
        <Route path="/privacy" element={<LegalPage type="privacy" />} />
        <Route path="/disclaimer" element={<LegalPage type="disclaimer" />} />

        <Route path="/dashboard" element={
          <ProtectedRoute>
            <UserDashboard />
          </ProtectedRoute>
        } />

        <Route path="/buy" element={
          <ProtectedRoute>
            <BuyPage />
          </ProtectedRoute>
        } />

        <Route path="/staking" element={
          <ProtectedRoute>
            <StakingPage />
          </ProtectedRoute>
        } />

        <Route path="/leaderboard" element={
          <ProtectedRoute>
            <LeaderboardPage />
          </ProtectedRoute>
        } />

        <Route path="/referrals" element={
          <ProtectedRoute>
            <ReferralsPage />
          </ProtectedRoute>
        } />

        <Route path="/withdrawals" element={
          <ProtectedRoute>
            <WithdrawalsPage />
          </ProtectedRoute>
        } />

        <Route path="/profile" element={
          <ProtectedRoute>
            <ProfilePage />
          </ProtectedRoute>
        } />

        <Route path="/admin" element={
          <ProtectedRoute adminOnly>
            <AdminDashboard />
          </ProtectedRoute>
        } />

        <Route path="/admin/users" element={
          <ProtectedRoute adminOnly>
            <AdminUsers />
          </ProtectedRoute>
        } />

        <Route path="/admin/plans" element={
          <ProtectedRoute adminOnly>
            <AdminPlans />
          </ProtectedRoute>
        } />

        <Route path="/admin/withdrawals" element={
          <ProtectedRoute adminOnly>
            <AdminWithdrawals />
          </ProtectedRoute>
        } />

        <Route path="/admin/referrals" element={
          <ProtectedRoute adminOnly>
            <AdminReferrals />
          </ProtectedRoute>
        } />

        <Route path="/admin/audit" element={
          <ProtectedRoute adminOnly>
            <AdminAuditLogs />
          </ProtectedRoute>
        } />

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
}

const ThemedToaster = () => {
  const { theme } = useTheme();
  return <Toaster position="top-right" theme={theme} richColors />;
};

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <div className="App">
            <AppRoutes />
            <ThemedToaster />
          </div>
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
